import { ColDef } from 'ag-grid-community';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import RemoteSourceSelectFilter from 'components/tables/filters/RemoteSourceSelectFilter';
import { useApi } from 'api/api-context';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import { ADD_DEVICE_PAGE_PATH } from 'routes/paths';
import DeviceSubtypeEqualsSelectFilter from 'components/tables/filters/device-subtype/DeviceSubtypeEqualsSelectFilter';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import DeviceFaultsSelectFilter from 'components/tables/filters/DeviceFaultsSelectFilter';
import { DeviceRow } from 'components/tables/data/device/types';
import DeviceNameColumn from 'components/tables/data/device/columns/DeviceNameColumn';
import OrganizationNameColumn from 'components/tables/data/device/columns/OrganizationNameColumn';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import DeviceTypeNameColumn from 'components/tables/data/device/columns/DeviceTypeNameColumn';
import FaultsColumn from 'components/tables/data/device/columns/FaultsColumn';
import InternalPossessionsNumberColumn from 'components/tables/data/device/columns/InternalPossessionsNumberColumn';
import ActionsColumn from 'components/tables/data/device/columns/ActionsColumn';
import DeviceSubtypeNameColumn from 'components/tables/data/device/columns/DeviceSubtypeNameColumn';
import ResponsiblePersonColumn from 'components/tables/data/device/columns/ResponsiblePersonColumn';
import ExternalIdColumn from 'components/tables/data/device/columns/ExternalIdColumn';
import DeviceIdColumn from 'components/tables/data/device/columns/DeviceIdColumn';
import BuildingColumn from 'components/tables/data/device/columns/BuildingColumn';
import FloorColumn from 'components/tables/data/device/columns/FloorColumn';
import RoomColumn from 'components/tables/data/device/columns/RoomColumn';
import ManufacturerColumn from 'components/tables/data/device/columns/ManufacturerColumn';
import RemoteSourceColumn from 'components/tables/data/device/columns/RemoteSourceColumn';
import SerialNumberColumn from 'components/tables/data/device/columns/SerialNumberColumn';
import ManufacturedColumn from 'components/tables/data/device/columns/ManufacturedColumn';
import { DataTableExport } from 'components/tables/ExportModal';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import {
  FEATURE_TOGGLE_BULK_CREATE_REVISION,
  FEATURE_TOGGLE_EVIDENCE_FIELDS_IN_DEVICE_TABLE,
  FEATURE_TOGGLE_PARENT_DEVICE,
  FEATURE_TOGGLE_REMOTE_SOURCES,
  FEATURE_TOGGLE_DEVICE_QR_CODES,
} from 'env';
import ParentDeviceSelectFilter from 'components/tables/filters/ParentDeviceSelectFilter';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import { useCounter } from '@mantine/hooks';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import { DeviceListResponse } from 'api/actions/device-list/device-list-response';
import { Group } from '@mantine/core';
import BulkCreateRevisionAction from 'components/tables/data/device/actions/bulk/BulkCreateRevisionAction';

type EvidenceField = NonNullable<DeviceListResponse[number]['evidenceFields']>[number];

interface DeviceTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

const advancedEvidenceFilterIcon =
  '<div title="Pokročilé filtrovanie"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-filter-spark"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 12.5v-.5l4.414 -4.414a2 2 0 0 0 .586 -1.414v-2.172h-16v2.227c0 .497 .185 .977 .52 1.345l4.48 4.928v8.5l2 -.667" /><path d="M18.5 22a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5" /></svg></div>';

/**
 * Table which displays devices.
 */
const DeviceTable = forwardRef<IDataTablePublic, DeviceTableProps>(
  ({ initialFilters, suppressProfileChangedNotification, suppressSaveProfiles }, ref) => {
    const { getAction, hasPermissionAnywhere } = useApi();
    const { tableActionMode } = useResponsiveDesign();

    const [evidenceFieldsColumns, setEvidenceFieldsColumns] = useState<ColDef[]>([]);
    const inventoryRequest = useRef(0);
    const [devices, setDevices] = useState<DeviceRow[]>([]);
    const [selectedRows, setSelectedRows] = useState<DeviceRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshToken, { increment: incrementRefreshToken }] = useCounter(0);
    const objectColumn = useObjectColumn({
      breadcrumbField: 'department.breadcrumbs',
      organizationFilterKey: 'organization.organizationId',
      permissionSlug: 'browse-devices',
      legacyColumnIds: ['department.departmentName'],
    });

    /**
     * The columns of the table.
     */
    const columns: ColDef[] = useMemo(
      () =>
        [
          {
            field: 'deviceId',
            headerName: 'ID',
            width: 76,
            minWidth: 76,
            maxWidth: 76,
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellRenderer: DeviceIdColumn,
          },
          {
            field: 'deviceName',
            headerName: 'Názov zariadenia',
            minWidth: 400,
            sortable: true,
            unSortIcon: true,
            filter: true,
            floatingFilter: FEATURE_TOGGLE_PARENT_DEVICE,
            floatingFilterComponent: ParentDeviceSelectFilter,
            cellRenderer: DeviceNameColumn,
          },
          {
            field: 'parentDevice.deviceId',
            headerName: 'ID nadradeného zariadenia',
            filter: true,
            hide: true,
          },
          {
            field: 'parentDevice.deviceName',
            headerName: 'Názov nadradeného zariadenie',
            hide: true,
          },
          {
            field: 'organization.organizationId',
            headerName: 'ID organizácie',
            hide: true,
            filter: OrganizationMultiColumnFilter,
          },
          {
            field: 'organization.organizationName',
            headerName: 'Organizácia',
            minWidth: 275,
            filter: true,
            floatingFilter: true,
            floatingFilterComponent: OrganizationMultiSelectFilter.configure({ permissionSlug: 'browse-devices' }),
            sortable: true,
            unSortIcon: true,
            cellRenderer: OrganizationNameColumn,
          },
          ...objectColumn.columns,
          {
            field: 'deviceType.deviceTypeId',
            hide: true,
            filter: true,
          },
          {
            field: 'deviceType.deviceTypeName',
            headerName: 'Zariadenie',
            minWidth: 220,
            filter: true,
            floatingFilter: true,
            floatingFilterComponent: DeviceTypeSelectFilter,
            sortable: true,
            unSortIcon: true,
            cellRenderer: DeviceTypeNameColumn,
          },
          {
            field: 'deviceSubtype.deviceTypeId',
            hide: true,
            filter: true,
          },
          {
            field: 'deviceSubtype.deviceTypeName',
            headerName: 'Typ zariadenia',
            minWidth: 250,
            filter: true,
            floatingFilter: true,
            floatingFilterComponent: DeviceSubtypeEqualsSelectFilter,
            sortable: true,
            unSortIcon: true,
            cellRenderer: DeviceSubtypeNameColumn,
          },
          {
            field: 'faultType',
            hide: true,
            filter: true,
          },
          {
            field: 'hasFaults',
            headerName: 'Má nedostatky',
            hide: true,
            filter: true,
          },
          {
            field: 'hasFaultsTranslation',
            headerName: 'Má nedostatky',
            hide: true,
          },
          {
            field: 'faults',
            headerName: 'Nedostatok',
            minWidth: 150,
            filter: true,
            floatingFilter: true,
            floatingFilterComponent: DeviceFaultsSelectFilter,
            cellRenderer: FaultsColumn,
          },
          {
            field: 'responsiblePerson.fullName',
            headerName: 'Zodpovedná osoba',
            minWidth: 300,
            cellRenderer: ResponsiblePersonColumn,
          },
          {
            field: 'internalPossessionsNumber',
            headerName: 'Int. č. majetku',
            minWidth: 160,
            cellRenderer: InternalPossessionsNumberColumn,
          },
          {
            field: 'building',
            headerName: 'Budova',
            minWidth: 250,
            sortable: true,
            unSortIcon: true,
            cellRenderer: BuildingColumn,
          },
          {
            field: 'floor',
            headerName: 'Podlažie',
            minWidth: 250,
            sortable: true,
            unSortIcon: true,
            cellRenderer: FloorColumn,
          },
          {
            field: 'room',
            headerName: 'Miestnosť',
            minWidth: 250,
            sortable: true,
            unSortIcon: true,
            cellRenderer: RoomColumn,
          },
          { field: 'longitude', headerName: 'Zemepisná dĺžka', hide: true },
          { field: 'latitude', headerName: 'Zemepisná šírka', hide: true },
          FEATURE_TOGGLE_REMOTE_SOURCES
            ? {
                field: 'remoteSource.remoteSourceId',
                headerName: 'ID externého zdroja',
                hide: true,
                filter: true,
              }
            : null,
          FEATURE_TOGGLE_REMOTE_SOURCES
            ? {
                field: 'remoteSource.name',
                headerName: 'Externý zdroj',
                minWidth: 200,
                filter: true,
                floatingFilter: true,
                floatingFilterComponent: RemoteSourceSelectFilter,
                sortable: true,
                unSortIcon: true,
                cellRenderer: RemoteSourceColumn,
              }
            : null,
          {
            valueGetter: ({ data: { status } }: { data: DeviceRow }) => (status ? '1' : '0'),
            field: 'status',
            headerName: 'Aktívna',
            filter: true,
            hide: true,
          },
          {
            field: 'externalId',
            headerName: 'Externé ID',
            minWidth: 160,
            cellRenderer: ExternalIdColumn,
          },
          FEATURE_TOGGLE_DEVICE_QR_CODES
            ? {
                field: 'qrId',
                headerName: 'QR ID',
                minWidth: 180,
                sortable: true,
                unSortIcon: true,
                filter: true,
                floatingFilter: true,
                icons: { filter: () => advancedEvidenceFilterIcon },
                cellRenderer: SimpleValueFormatter,
              }
            : null,
          FEATURE_TOGGLE_DEVICE_QR_CODES
            ? {
                field: 'qrUrl',
                headerName: 'QR URL',
                minWidth: 320,
                sortable: true,
                unSortIcon: true,
                filter: true,
                floatingFilter: true,
                hide: true,
                icons: { filter: () => advancedEvidenceFilterIcon },
              }
            : null,
          {
            field: 'manufacturer',
            headerName: 'Výrobca',
            minWidth: 250,
            sortable: true,
            unSortIcon: true,
            cellRenderer: ManufacturerColumn,
          },
          {
            field: 'serialNumber',
            headerName: 'Výrobné číslo',
            minWidth: 200,
            sortable: true,
            unSortIcon: true,
            cellRenderer: SerialNumberColumn,
          },
          {
            field: 'manufactured',
            headerName: 'Rok výroby',
            minWidth: 150,
            sortable: true,
            unSortIcon: true,
            cellRenderer: ManufacturedColumn,
          },
          { field: 'responsiblePersonNote', headerName: 'Poznámka k zodpovednej osobe', hide: true },
          { field: 'internalNote', headerName: 'Poznámka k int. číslu majetku', hide: true },
          {
            field: 'isDiscarded',
            headerName: 'Vyradené',
            hide: true,
          },
          {
            field: 'lastRevision.revisionId',
            headerName: 'ID poslednej revízie',
            hide: true,
          },
          {
            field: 'lastRevision.revisionName',
            headerName: 'Názov poslednej revízie',
            hide: true,
          },
          {
            field: 'importId',
            headerName: 'ID importu',
            filter: true,
            hide: true,
          },
          ...evidenceFieldsColumns,
          {
            field: '_actions',
            headerName: '',
            pinned: 'right',
            width: getResponsiveActionsColumnWidth(194, tableActionMode),
            minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
            maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
            cellRenderer: ActionsColumn,
          },
        ].filter(Boolean) as ColDef[],
      [evidenceFieldsColumns, objectColumn.columns, tableActionMode]
    );

    const exportColumns = useMemo(
      () => [
        'deviceId',
        'deviceName',
        'parentDevice.deviceId',
        'parentDevice.deviceName',
        'organization.organizationId',
        'organization.organizationName',
        ...objectColumn.columnKeys,
        'deviceType.deviceTypeName',
        'deviceSubtype.deviceTypeName',
        'hasFaultsTranslation',
        'internalPossessionsNumber',
        'internalNote',
        'building',
        'room',
        'floor',
        'longitude',
        'latitude',
        'responsiblePerson.fullName',
        'externalId',
        ...(FEATURE_TOGGLE_DEVICE_QR_CODES ? ['qrId', 'qrUrl'] : []),
        'remoteSource.remoteSourceId',
        'remoteSource.name',
        'manufacturer',
        'serialNumber',
        'manufactured',
        'responsiblePersonNote',
        'isDiscarded',
        'lastRevision.revisionId',
        'lastRevision.revisionName',
        ...evidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
      ],
      [evidenceFieldsColumns, objectColumn.columnKeys]
    );

    const dataExport: DataTableExport<DeviceRow> = useMemo(
      () => ({
        modalTitle: 'Exportovať zariadenia',
        fileName: 'zariadenia.xlsx',
        columnKeys: exportColumns,
        columnReplacements: { ...objectColumn.columnReplacements, faults: ['hasFaultsTranslation'] },
        ready: objectColumn.exportReady,
        error: objectColumn.exportError,
        initialSelectedColumns: (rows) =>
          exportColumns.filter((column) => {
            if (column.startsWith('evidenceFields.')) {
              // Only include evidence fields which have data in at least one row
              const fieldKey = column.replace('evidenceFields.', '');

              return rows.some((row) => {
                const value = (row.evidenceFields as any)?.[fieldKey];
                return value !== undefined && value !== null && value !== '';
              });
            }

            return true;
          }),
      }),
      [exportColumns, objectColumn.columnReplacements, objectColumn.exportError, objectColumn.exportReady]
    );

    const fetchDevices = useCallback(async () => {
      const request = ++inventoryRequest.current;
      const action = getAction('DeviceList');

      const devices = await action();
      if (request !== inventoryRequest.current) return;

      const evidenceFieldMap = new Map<string, EvidenceField>();

      const augmentedDevices = devices.map((device) => {
        const evidenceFields = device.evidenceFields?.reduce((acc, field) => {
          if (!field.fieldName || !field.evidenceFieldId) {
            return acc;
          }

          const key = String(field.evidenceFieldId);
          const value = getEvidenceFieldValue(field) ?? '';

          evidenceFieldMap.set(key, field);

          acc[key] = value;

          return acc;
        }, {} as Record<string, string | number | boolean>);

        return {
          ...device,
          faultType: device.hasUnfixedFaults ? 'with-faults' : 'no-faults',
          hasFaultsTranslation: device.hasUnfixedFaults ? 'Áno' : 'Nie',
          isDiscarded: device.status ? 'Nie' : 'Áno',
          evidenceFields,
        };
      });

      const evidenceFieldsColumns = Array.from(evidenceFieldMap).map(
        ([key, { fieldName: name, precision = 0 }]) =>
          ({
            field: `evidenceFields.${key}`,
            headerName: name,
            minWidth: 200,
            sortable: true,
            unSortIcon: true,
            filter: true,
            floatingFilter: true,
            hide: !FEATURE_TOGGLE_EVIDENCE_FIELDS_IN_DEVICE_TABLE,
            filterValueGetter: ({ data: { evidenceFields } }: { data: DeviceRow }) => (evidenceFields as any)[key],
            cellRenderer: ({ data: { evidenceFields } }: { data: DeviceRow }) => {
              const value = (evidenceFields as any)[key];
              return <SimpleValueFormatter value={value} precision={precision} />;
            },
            icons: {
              filter: () =>
                '<div title="Pokročilé filtrovanie"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-filter-spark"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 12.5v-.5l4.414 -4.414a2 2 0 0 0 .586 -1.414v-2.172h-16v2.227c0 .497 .185 .977 .52 1.345l4.48 4.928v8.5l2 -.667" /><path d="M18.5 22a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5" /></svg></div>',
            },
          } satisfies ColDef)
      );

      setEvidenceFieldsColumns(evidenceFieldsColumns);
      setDevices(augmentedDevices as any);
      setLoading(false);
      incrementRefreshToken();
    }, [getAction]);

    const action = useCallback(() => Promise.resolve(devices), [devices]);

    useEffect(() => {
      fetchDevices();
      return () => {
        inventoryRequest.current += 1;
      };
    }, [fetchDevices]);

    if (loading) {
      return null;
    }

    const tableId = loading ? 'devices-loading' : 'devices';

    return (
      <DataTable
        key={`${tableId}-${refreshToken}`}
        ref={ref}
        tableId={tableId}
        title="Zariadenia"
        addButtonText="Pridať nové"
        toggleDiscardedLabel="Zobraziť vyradené"
        columns={columns}
        action={action}
        addButtonTarget={ADD_DEVICE_PAGE_PATH.original}
        hideAddButton={!hasPermissionAnywhere('manage-devices')}
        selectedRows={selectedRows}
        onSelectedRowsChange={setSelectedRows}
        initialFilters={initialFilters}
        dataExport={dataExport}
        profileColumnAliases={objectColumn.profileColumnAliases}
        profileMigrationReady={objectColumn.profileMigrationReady}
        suppressSaveProfiles={suppressSaveProfiles}
        suppressProfileChangedNotification={suppressProfileChangedNotification}
        bulkActions={
          FEATURE_TOGGLE_BULK_CREATE_REVISION ? (
            <Group spacing={16}>
              <BulkCreateRevisionAction devices={selectedRows} kind="revision" />
              <BulkCreateRevisionAction devices={selectedRows} kind="form" />
            </Group>
          ) : undefined
        }
      />
    );
  }
);

export default DeviceTable;
